import React, { useState, useEffect } from "react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Button,
  Link as MUILink,
  useTheme,
  Snackbar,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { motion } from "framer-motion";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SortlyBooksLogo from "../components/SortlyBooksLogo";
import { linkUserIfInvited } from "../utils/inviteHelpers";
import api from "../services/api";

const getPasswordStrength = (password) => {
  let score = 0;
  if (password.length >= 6) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;

  if (score <= 1) return { label: "Weak", color: "#e57373" };
  if (score === 2) return { label: "Medium", color: "#ffb74d" };
  return { label: "Strong", color: "#22c55e" };
};

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

export default function SignupForm() {
  const [error, setError] = useState("");
  const [successSnackbar, setSuccessSnackbar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    label: "",
    color: "",
  });

  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    if (window.plausible) window.plausible("SignupStarted");
  }, []);

  const handleSubmit = async (values) => {
    const { email, password } = values;
    setLoading(true);
    setError("");
    const trimmedEmail = email.toLowerCase().trim();

    try {
      const cred = await createUserWithEmailAndPassword(
        auth,
        trimmedEmail,
        password
      );

      const user = cred.user;

      const wasInvited = await linkUserIfInvited(user);

      if (!wasInvited) {
        // Not an invited user — create their user doc
        await setDoc(doc(db, "users", user.uid), {
          email: trimmedEmail,
          createdAt: serverTimestamp(),
          trialStartedAt: serverTimestamp(),
          plan: "free",
          source: "web",
        });

        try {
          console.log("Calling signup API for new user:", trimmedEmail);

          await api.post("/auth/signup", {
            email: trimmedEmail,
            uid: user.uid,
            role: "freelancer",
          });
        } catch (apiErr) {
          // Optional: log this for diagnostics
          console.error("Signup API call failed:", apiErr);
        }
      }

      setSuccessSnackbar(true);
      setTimeout(() => navigate("/onboarding"), 1500);
    } catch (err) {
      console.error("🔥 Firebase signup error:", err);
      setError(err.message || "Signup failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f9fbff",
        px: 2,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 420,
            borderRadius: "16px",
            boxShadow: "0 6px 30px rgba(0,0,0,0.05)",
            backgroundColor: "#fff",
            textAlign: "center",
          }}
        >
          <Box sx={{ mb: 2 }}>
            <SortlyBooksLogo />
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              Your smart receipt & report assistant
            </Typography>
          </Box>

          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ mb: 1.5, fontFamily: "Poppins, Inter" }}
          >
            Create your free account
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", mb: 2, display: "block" }}
          >
            Trusted by 1,000+ freelancers and small businesses
          </Typography>

          <Formik
            initialValues={{ email: "", password: "" }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              handleChange,
              handleBlur,
              errors,
              touched,
              isValid,
            }) => (
              <Form noValidate>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(errors.email && touched.email)}
                  helperText={touched.email && errors.email}
                  margin="normal"
                  sx={{ backgroundColor: "#f1f5f9", borderRadius: 2 }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={values.password}
                  onChange={(e) => {
                    handleChange(e);
                    setPasswordStrength(getPasswordStrength(e.target.value));
                  }}
                  onBlur={handleBlur}
                  error={Boolean(errors.password && touched.password)}
                  helperText={touched.password && errors.password}
                  margin="normal"
                  sx={{ backgroundColor: "#f1f5f9", borderRadius: 2 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {values.password && (
                  <Box sx={{ mt: 1.5, textAlign: "left" }}>
                    <Box
                      sx={{
                        height: 6,
                        borderRadius: 4,
                        backgroundColor: passwordStrength.color,
                        transition: "background-color 0.3s",
                        width: "100%",
                        mb: 0.5,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: passwordStrength.color }}
                    >
                      {passwordStrength.label} password
                    </Typography>
                  </Box>
                )}

                {error && (
                  <Alert severity="error" sx={{ mt: 2, textAlign: "left" }}>
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={!isValid || loading}
                  sx={{
                    mt: 3,
                    mb: 2,
                    borderRadius: "12px",
                    fontWeight: 600,
                    py: 1.2,
                    backgroundColor: "#1e3a8a",
                    "&:hover": { backgroundColor: "#1e40af" },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    "Sign Up"
                  )}
                </Button>

                <Typography align="center" sx={{ fontSize: 14 }}>
                  Already have an account?{" "}
                  <MUILink component={Link} to="/login" underline="hover">
                    Log in
                  </MUILink>
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 3, fontSize: 11 }}
                >
                  By signing up, you agree to our{" "}
                  <MUILink component={Link} to="/terms" underline="hover">
                    Terms
                  </MUILink>{" "}
                  and{" "}
                  <MUILink component={Link} to="/privacy" underline="hover">
                    Privacy Policy
                  </MUILink>
                  .
                </Typography>
              </Form>
            )}
          </Formik>
        </Paper>
      </motion.div>

      <Snackbar
        open={successSnackbar}
        autoHideDuration={2000}
        onClose={() => setSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccessSnackbar(false)}
          severity="success"
          variant="filled"
        >
          🎉 Account created successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
}
